import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {AuthService, Breadcrumb, RoutingService} from '@spartacus/core';
import {LaunchDialogService} from '@spartacus/storefront';
import {BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {map, take} from 'rxjs/operators';

import {AimoCmsService} from '../../../cms-structure/routing/aimo-cms.service';
import {AimoRoutingService} from '../../../cms-structure/routing/aimo-routing.service';
import {
    AimoCategory,
    AimoClassification,
    AimoFeature,
    AimoProduct,
    IngredientData,
    IngredientOriginData
} from '../../../model/product.model';
import {AimoUser} from '../../../model/user.model';
import {AimoGTMProductAttributes, GTMCartType, GTMItemListId} from '../../../service/gtm/aimo-gtm.model';
import {AimoGtmService} from '../../../service/gtm/aimo-gtm.service';
import {AimoCurrentProductService} from '../../../service/product/aimo-current-product.service';
import {
    AimoProductService,
    CROSSSELING_PRODUCTS,
    SIMILAR_PRODUCTS,
} from '../../../service/product/aimo-product.service';
import {AimoProductComparisonService} from '../../../service/user/aimo-product-comparison.service';
import {AimoUserService} from '../../../service/user/aimo-user.service';
import {DateUtils} from '../../../shared/util/date-utils';
import {
    AimoOrderTemplateDialogData,
    ORDER_TEMPLATE,
    ProductWrapper,
} from '../../order-template/order-template-modal/aimo-order-template-layout.config';
import {AimoCMSCarouselComponentData} from '../../shared/carousel/cms/aimo-cms-carousel.component';

import {AimoProductSummaryDialogData, PRODUCT_SUMMARY} from './product-summary-layout.config';
import {AimoGtmStoredParams} from '../../../model/cart.model';
import {AimoAssortmentService, LabelSuffix} from '../../../service/product/aimo-assortment.service';
import {AimoNavigationService} from '../../../service/navigation/aimo-navigation.service';
import {AimoUxTypeService} from '../../../service/general/aimo-ux-type.service';

@Component({
    selector: 'aimo-product-summary',
    templateUrl: './aimo-product-summary.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AimoProductSummaryComponent implements OnDestroy, OnInit {
    CROSSSELING_PRODUCTS = CROSSSELING_PRODUCTS;
    SIMILAR_PRODUCTS = SIMILAR_PRODUCTS;

    private subscription = new Subscription();
    modal: boolean = false;
    addToCartCallBack: BehaviorSubject<number>;
    onProductChangeCallBack: BehaviorSubject<AimoProduct>;
    openModalAfterClose: string;
    focusIdAfterClose: string;
    user$: Observable<AimoUser>;
    myAssortmentLabelSuffix$: Observable<LabelSuffix>;
    activeTab: AimoCMSCarouselComponentData = null;
    isMobileApp$: Observable<boolean>;
    ingredientsOpened: boolean = false;

    nutritionFactNames = [
        'FAT',
        'FASAT',
        'FAPU',
        'FAPUCIS',
        'CHOAVL',
        'SUGAR-',
        'LACS',
        'PRO-',
        'SALTEQ',
        'FIBTG',
        'POLYL',
        'STARCH',
    ];

    vitaminNames = [
        'VITA-',
        'VITC',
        'VITB9',
        'VITB12',
        'VITB2',
        'VITD',
        'VITE',
        'VITB1',
        'VITB3',
        'VITB6-',
        'VITB5',
        'VITH',
        'VITK',
    ];

    mineralNames = ['NA', 'IODIZED_SALT', 'CA', 'K', 'MG', 'P', 'ZN', 'FE', 'CLD', 'FD', 'MN', 'SE', 'CU'];

    @ViewChild('productLink')
    productLink: ElementRef;

    @ViewChild('modalContent')
    modalContentRef: ElementRef;

    loggedUser$: Observable<boolean>;
    product$: Observable<AimoProduct | null>;

    crossSelling = GTMItemListId.cross_selling;
    alternative = GTMItemListId.alternative;
    productSummary = GTMItemListId.product_summary;

    inheritedGTMProductAttributes: AimoGTMProductAttributes = {gtmParams: {gtmItemListId: this.productSummary}};

    private breadcrumbs: Breadcrumb[];

    constructor(
        protected launchDialogService: LaunchDialogService,
        protected aimoUserService: AimoUserService,
        protected productService: AimoProductService,
        @Inject(DOCUMENT) private document: Document,
        protected el: ElementRef,
        protected routingService: RoutingService,
        protected productComparisonService: AimoProductComparisonService,
        protected aimoRoutingService: AimoRoutingService,
        protected auth: AuthService,
        protected gtmService: AimoGtmService,
        protected cdr: ChangeDetectorRef,
        protected currentProductService: AimoCurrentProductService,
        protected cmsService: AimoCmsService,
        protected assortmentService: AimoAssortmentService,
        protected navigationService: AimoNavigationService,
        protected aimoUxTypeService: AimoUxTypeService,
    ) {
    }

    ngOnInit(): void {
        this.isMobileApp$ = this.aimoUxTypeService.isMobileApp();
        this.user$ = this.aimoUserService.get();
        this.loggedUser$ = this.auth.isUserLoggedIn();
        this.myAssortmentLabelSuffix$ = this.assortmentService.getMyAssortmentLabelSuffix();
        this.launchDialogService.data$.pipe(take(1)).subscribe((dialogData: AimoProductSummaryDialogData) => {
            if (dialogData?.product) {
                this.modal = true;
                this.product$ = dialogData.product.pipe(map((p) => ({...p})));
                this.addToCartCallBack = dialogData.addToCartCallBack;
                this.onProductChangeCallBack = dialogData.onProductChangeCallBack;
                this.focusIdAfterClose = dialogData.focusIdAfterClose;
                this.openModalAfterClose = dialogData.openModalAfterClose;
                this.inheritedGTMProductAttributes = dialogData.gtmProductAttributes;
                if (dialogData.scrollToId) {
                    setTimeout(() => this.scrollTo(dialogData.scrollToId), 1500); // 500 or 1000 is too fast sometimes for similar products container
                }
            } else {
                this.product$ = this.currentProductService.getProduct().pipe(map((p) => ({...p})));
            }
        });
        setTimeout(() => this.getTheFocusBack(), 100);
    }

    getBreadcrumbs(product: AimoProduct): Breadcrumb[] {
        if (this.breadcrumbs === undefined) {
            const breadcrumbs = [];
            breadcrumbs.push({facetValueName: product.name, facetValueCode: product.code});
            product.categories
                ?.filter((cat) => !(cat as AimoCategory).dummyCategory)
                .forEach((cat) => {
                    breadcrumbs.push({
                        facetValueName: cat.name || cat.code,
                        facetValueCode: cat.code,
                    } as Breadcrumb);
                });
            this.breadcrumbs = breadcrumbs.reverse();
        }
        return this.breadcrumbs;
    }

    openProductInSmartEdit(product: AimoProduct): void {
        this.modal = false;
        this.launchDialogService.clear(PRODUCT_SUMMARY);
        this.launchDialogService.closeDialog('summary_closed');
        this.routingService.go(product.url);
    }

    isInSmartEditMode(): boolean {
        return this.navigationService.isInSmartEditMode();
    }

    onProductChange(quantity: number): void {
        this.addToCartCallBack?.next(quantity);
    }

    ngOnDestroy(): void {
        this.closeDialog();
        this.subscription.unsubscribe();
    }

    toggleFavorite(product: AimoProduct): void {
        this.aimoUserService.toggleFavorite(product);
        product.favorite = !product.favorite;
    }

    convertDate(d: string): Date {
        return DateUtils.convertDate(d);
    }

    getClassificationClass(product: AimoProduct, code: string): AimoClassification {
        return this.productService.getClassificationClass(product, code);
    }

    getNutritionalContent(product: AimoProduct, code: string): AimoFeature {
        return this.productService.getNutritionalContent(product, code);
    }

    getClassificationValueList(product: AimoProduct, code: string): string {
        return this.productService.getClassificationValueList(product, code);
    }

    getClassificationNameList(product: AimoProduct, code: string): string {
        return this.productService.getClassificationNameList(product, code);
    }

    getClassification(product: AimoProduct, code: string): AimoFeature[] {
        return this.productService.getClassification(product, code);
    }

    getClassificationName(product: AimoProduct, code: string): string {
        return this.productService.getClassificationName(product, code);
    }

    getClassificationValueWithUnit(product: AimoProduct, code: string, trimZeros?: boolean): string {
        const f = this.productService.getClassificationValue(product, code);
        if (f) {
            let value = f.value;
            if (trimZeros) {
                value = f.value.replace(/\..*/, '');
            }
            return value.replace('.', ',') + ' ' + f.unit?.name;
        }
        return '';
    }

    print(): void {
        this.document.defaultView.window.print();
    }

    closeDialog(): void {
        this.launchDialogService.clear(PRODUCT_SUMMARY);
        (this.launchDialogService as any)._dataSubject.next(undefined);
        this.launchDialogService.closeDialog('summary_closed');
        if (this.openModalAfterClose) {
            this.launchDialogService.openDialogAndSubscribe(this.openModalAfterClose, undefined);
        }
        if (this.focusIdAfterClose && this.document.getElementById(this.focusIdAfterClose)) {
            this.document.getElementById(this.focusIdAfterClose).focus();
        }
        //   this.onCloseCallBack.next(this.getAimoProduct());
    }

    getFullUrl(): string {
        return this.productLink
            ? (this.productLink.nativeElement as HTMLAnchorElement)?.href
            : this.document.location.href;
    }

    @HostListener('click', ['$event'])
    clickout(event: UIEvent): void {
        // eslint-disable-next-line
        if ((event.target as any).tagName === 'A' || (event.target as any).tagName === this.el.nativeElement.tagName) {
            // close when links are clicked
            this.closeDialog();
        }
    }

    similarProducts$: Observable<AimoCMSCarouselComponentData>;

    getSimilarProducts(product: AimoProduct): Observable<AimoCMSCarouselComponentData> {
        if (this.similarProducts$ == undefined) {
            let idx = 0;
            this.similarProducts$ = this.cmsService.getComponentData('AimoSimilarProductsCarousel').pipe(
                map((data) => ({
                    ...data,
                    carouselProducts: this.productService.getSimilarProducts(product)?.map((p) =>
                        of({
                            ...p,
                            gtmProductAttributes: this.createGtmProductAttributes(idx++, this.alternative, p),
                        }),
                    ),
                })),
            );
        }
        return this.similarProducts$;
    }

    crossSellingProducts$: Observable<AimoCMSCarouselComponentData>;

    getCrossSellingProducts(product: AimoProduct): Observable<AimoCMSCarouselComponentData> {
        if (this.crossSellingProducts$ == undefined) {
            let idx = 0;
            this.crossSellingProducts$ = this.cmsService.getComponentData('AimoCrossellingProductsCarousel').pipe(
                map((data) => ({
                    ...data,
                    carouselProducts: this.productService.getCrossSellingProducts(product)?.map((p) =>
                        of({
                            ...p,
                            gtmProductAttributes: this.createGtmProductAttributes(idx++, this.crossSelling, p),
                        }),
                    ),
                })),
            );
        }
        return this.crossSellingProducts$;
    }

    truncate(text: string): string {
        if (text.length > 400) {
            return text.substring(0, 400) + '\u2026';
        }
        return null;
    }

    showDescription = false;

    toggleDescription(): void {
        this.showDescription = !this.showDescription;
    }

    addToComparison(product: AimoProduct): void {
        this.productComparisonService.addProductToComparison(product.code);
    }

    scrollTo(id: string): void {
        this.document.querySelector('#' + id)?.scrollIntoView();
    }

    createGtmProductAttributes(idx: number, listId: GTMItemListId, product: AimoProduct): AimoGTMProductAttributes {
        const base = this.gtmService.createProductGtmAttributesBase(product, listId);
        return {
            ...this.inheritedGTMProductAttributes,
            ...base,
            gtmParams: {
                ...this.inheritedGTMProductAttributes?.gtmParams,
                ...base.gtmParams,
            },
            index: idx,
            cart_type: GTMCartType.cart,
        } as AimoGTMProductAttributes;
    }

    addToOrderTemplate(product: AimoProduct, sourceId: string): void {
        this.launchDialogService.openDialogAndSubscribe(ORDER_TEMPLATE, undefined, {
            addToTemplateProducts: [new ProductWrapper(product, 1)],
            createNew: false,
            openProductModalAfterClose: this.modal,
            focusIdAfterClose: sourceId,
        } as AimoOrderTemplateDialogData);
    }

    @ViewChild('closeButton')
    closeButton: ElementRef;

    getTheFocusBack(event?: FocusEvent): void {
        event?.preventDefault();
        if (this.closeButton) {
            this.closeButton.nativeElement.focus();
        } else {
            setTimeout(() => this.getTheFocusBack(event), 100);
        }
    }

    isPikatukku(): boolean {
        return this.aimoRoutingService.isPikatukku();
    }

    safetyDataExport(product: AimoProduct): void {
        this.productService.safetyDataExport(product.code, product.name);
    }

    toggleAvailabilityNotification(product: AimoProduct): void {
        this.productService.toggleAvailabilityNotificationMessage(product, () => {
            this.cdr.detectChanges();
            this.onProductChangeCallBack?.next(product);
        });
    }

    getLastPurchasedTranslationKey(product: AimoProduct): string {
        return this.productService.getLastPurchasedTranslationKey(this.convertDate(product.lastPurchased));
    }

    getGtmParams(itemListId: GTMItemListId): AimoGtmStoredParams {
        return {
            gtmItemListId: itemListId,
        } as AimoGtmStoredParams;
    }

    protected getQueryParams(gtmParams: AimoGtmStoredParams | undefined) {
        return gtmParams
            ? {
                item_list_id: gtmParams.gtmItemListId,
                promotion_id: gtmParams.gtmPromotionId,
                promotion_name: gtmParams.gtmPromotionName,
                creative_slot: gtmParams.gtmCreativeSlot,
            }
            : {};
    }

    setActiveTab(tab: AimoCMSCarouselComponentData, $event: MouseEvent): void {
        $event.preventDefault();
        this.activeTab = tab;
    }

    protected isActiveTab(tab: AimoCMSCarouselComponentData, idx: number) {
        return tab === this.activeTab || (this.activeTab == null && idx === 0);
    }

    protected toggleIngredients(product: AimoProduct) {
        this.ingredientsOpened = !this.ingredientsOpened;
    }

    protected getIngredientOrigins(ingredient: IngredientData): string {
        let ret = ' ';
        if (ingredient.percentage) {
            ret += ingredient.percentage + '% ';
        }

        ret += ingredient.origins.map((origin) => origin.description + this.getOriginCountries(origin)).join(', ');
        return ret;
    }

    protected getOriginCountries(origin: IngredientOriginData): string {
        if (!origin.countries || origin.countries.length === 0) {
            return '';
        }
        return ' (' + origin.countries.map(o => o.name).join(', ') + ')';
    }
}
